<template>
  <section class="user_management__detail">
    <header class="content__header">
      <h2>
        <ui-button iconName="icon--prev-gray-size16" circle hideLabel
          @click="$emit('onBack')"/>
        {{ $t('userInfo.userManagement.user') }}
      </h2>
    </header>

    <div class="content__body">
      <div class="content__top">
        <content-box class="content__user-info" :width="'470px'" :height="'440px'">
          <h4 v-html="$t('userInfo.userManagement.basicInformation')"></h4>
          <form class="user-info-form" v-if="user" @submit.prevent="onUpdateUserInfo">
            <fieldset>
              <div class="input__text">
                <label v-html="$t('common.id')"></label>
                <div class="input">
                  <div class="readonly"
                       v-html="user.userId"
                       :title="user.userId"></div>
                </div>
              </div>
              <div class="input__text">
                <label for="email" v-html="$t('common.email')"></label>
                <div class="input">
                  <ui-input-text :type="'text'"
                                 id="email"
                                 :placeholder="$t('common.email')"
                                 :modelError="modelError.email"
                                 :modelValue="model.email"
                                 @input="model.email = $event"
                                 @onFocus="modelError.email = ''"></ui-input-text>
                </div>
              </div>
              <div class="input__text">
                <label for="branchName" v-html="$t('common.corporation')"></label>
                <div class="input">
                  <ui-input-text :type="'text'"
                                 id="branchName"
                                 :placeholder="$t('common.corporation')"
                                 :modelError="modelError.branchName"
                                 :modelValue="model.branchName"
                                 @input="model.branchName = $event"
                                 @onFocus="modelError.branchName = ''"></ui-input-text>
                </div>
              </div>
              <div class="input__timezone">
                <label for="timezone" v-html="$t('common.timezone')"></label>
                <div class="input">
                  <timezone-selectbox id="timezone"
                    v-model="model.timezoneName"
                    :width="'100%'"
                    @change="selectedTimezone" />
                </div>
              </div>
              <div class="input__text">
                <label v-html="$t('common.registrationDate')"></label>
                <div class="input">
                  <div class="readonly">{{ user.regDate | dateFormatTimezone(userData.timezoneName, 'YYYY.MM.DD') }}</div>
                </div>
              </div>
            </fieldset>
            <div class="button--wrap">
              <ui-button class="btn__register" :buttonType="'submit'" :primary="true">
                {{ $t('common.save') }}
              </ui-button>
            </div>
          </form>
        </content-box>
        <content-box class="content__user-permission" v-if="user" :width="'calc(100% - 500px)'" :height="'440px'">
          <h4 v-html="$t('userInfo.userManagement.roleAndPermission')"></h4>
          <div class="input__text">
            <label v-html="$t('common.role')"></label>
            <div class="input">
              <div class="readonly" v-html="$t('userInfo.requestManagement.approve.step1.' + (user.updatePermission ? 'localManager' : 'viewer') + '.title')"></div>
            </div>
          </div>
          <div class="input__list">
            <label v-html="$t('userInfo.userManagement.accessibleCountryCode')"></label>
            <ul class="site-code-list"
                :class="{ long: user.originalSiteCodeList.includes('ALL') || user.etcSiteCodeList.length < 1 }">
              <li v-for="code in user.originalSiteCodeList" v-bind:key="code">
                {{code}} <span v-if="countryCodeList[code]">[{{countryCodeList[code]}}]</span>
              </li>
            </ul>
          </div>
          <div class="input__list">
            <label v-html="$t('userInfo.userManagement.accessibleEtcGroup')"></label>
            <div class="readonly" v-if="user.originalSiteCodeList.includes('ALL')"
              v-html="$t('userInfo.userManagement.accessibleToAllEtcGroups')"></div>
            <div class="readonly" v-else-if="user.etcSiteCodeList.length < 1"
              v-html="$t('userInfo.userManagement.noEtcGroupsAccessible')"></div>
            <ul class="site-code-list" v-else>
              <li v-for="code in user.etcSiteCodeList" v-bind:key="code">
                {{code}}
              </li>
            </ul>
          </div>
          <div class="button--wrap">
            <ui-button class="btn__register" @click="isUserRoleEditing = true">
              {{ $t('common.edit') }}
            </ui-button>
          </div>
        </content-box>
      </div>

      <content-box class="content__apply-list" :width="'100%'" v-if="(user && user.updatePermission) || (applyList && applyList.length && user.updatePermission !== 'localManager')">
        <h4 v-html="$t('userInfo.userManagement.registeredPage')"></h4>

        <div class="input__search">
          <input type="text"
                 class="search-input"
                 :value="searchKeyword"
                 @input="searchKeyword = $event.target.value.trim().toLowerCase()">
          <i class="icon icon--search"></i>
        </div>

        <div class="item__body">
          <table>
            <colgroup>
              <col width="128px">
              <col :width="$i18n.locale === 'en' ? '118px' : '90px'">
              <col width="108px">
              <col width="*">
              <col width="88px">
              <col width="88px">
              <col width="88px">
              <col width="95px">
            </colgroup>
            <thead>
            <tr>
              <th class="align-left">
                <sort-options
                  columnName="domain"
                  :sortColumn="sortColumn"
                  :isSortAsc="isSortAsc"
                  @onSortOptionChange="onSortOptionChange">
                  <span v-html="$t('common.domain')"></span></sort-options>
              </th>
              <th class="align-center">
                <sort-options
                  columnName="siteCode"
                  :sortColumn="sortColumn"
                  :isSortAsc="isSortAsc"
                  @onSortOptionChange="onSortOptionChange">
                  <span v-html="$t('common.countryCode')"></span></sort-options>
              </th>
              <th class="align-center">
                <sort-options
                  columnName="category"
                  :sortColumn="sortColumn"
                  :isSortAsc="isSortAsc"
                  @onSortOptionChange="onSortOptionChange">
                  <span v-html="$t('common.group')"></span></sort-options>
              </th>
              <th class="align-left">
                <sort-options
                  columnName="pageName"
                  :sortColumn="sortColumn"
                  :isSortAsc="isSortAsc"
                  @onSortOptionChange="onSortOptionChange">
                  <span v-html="$t('common.page')"></span></sort-options>
              </th>
              <th>
                <sort-options
                  columnName="state"
                  :sortColumn="sortColumn"
                  :isSortAsc="isSortAsc"
                  @onSortOptionChange="onSortOptionChange">
                  <span v-html="$t('common.table.header.analysisStatusNoWrap')"></span></sort-options>
              </th>
              <th>
                <sort-options
                  columnName="startDate"
                  :sortColumn="sortColumn"
                  :isSortAsc="isSortAsc"
                  @onSortOptionChange="onSortOptionChange">
                  <span v-html="$t('common.table.header.startDate')"></span></sort-options>
              </th>
              <th>
                <sort-options
                  columnName="endDate"
                  :sortColumn="sortColumn"
                  :isSortAsc="isSortAsc"
                  @onSortOptionChange="onSortOptionChange">
                  <span v-html="$t('common.table.header.endDate')"></span></sort-options>
              </th>
              <th>
                <span v-html="$t('common.table.header.report')"></span>
              </th>
            </tr>
            </thead>
            <tbody v-if="hasResult && applyList && applyList.length">
              <tr v-for="apply in applyList" v-bind:key="apply._id" v-show="!apply.isHide">
                <td class="align-left ellipsis">
                  <text-highlight :searchKeyword="searchKeyword"
                                  :title="apply.domain"
                                  :textItem="apply.domain"></text-highlight>
                </td>
                <td class="align-center ellipsis">
                  <text-highlight :searchKeyword="searchKeyword"
                                  :title="apply.siteCode"
                                  :textItem="apply.siteCode"></text-highlight>
                </td>
                <td class="align-center ellipsis">
                  <text-highlight :searchKeyword="searchKeyword"
                                  :title="apply.category"
                                  :textItem="apply.category"></text-highlight>
                </td>
                <td class="align-left data__page"
                    @click.stop="onViewApplyClick(apply._id)">
                  <p class="page-name ellipsis">
                    <text-highlight :searchKeyword="searchKeyword"
                                    :title="apply.pageName"
                                    :textItem="apply.pageName || 'untitled'"></text-highlight>
                  </p>
                  <p class="page-url">
                    <span class="match-type">{{ apply.matchType | matchType(true) }}</span>
                    <span class="url ellipsis" :title="apply.url">{{ apply.url }}</span>
                  </p>
                </td>
                <td class="align-center">
                  <p class="data__status" :class="apply.state">
                    {{ $t(`page.state.${apply.state}`) }}
                  </p>
                </td>
                <td class="align-center data__start-date">
                  <p class="data__start-date">
                  {{ (apply.startDate || apply.regDate) | dateFormatTimezone(userData.timezoneName) }}
                  </p>
                </td>
                <td class="align-center">
                  <p class="data__end-date" v-if="apply.endDate">
                    {{ apply.endDate | dateFormatTimezone(userData.timezoneName) }}
                  </p>
                  <p class="data__end-date" v-else>
                    {{ $t('common.nonStop') }}
                  </p>
                </td>
                <td class="align-center">
                  <p class="data__confirm">
                    <ui-button
                      iconName="icon--dashboard-confirm"
                      size="small"
                      @click.stop="onViewApplyClick(apply._id)">
                      {{ $t('common.view') }}
                    </ui-button>
                  </p>
                </td>
              </tr>
            </tbody>
            <tbody v-else-if="applyList">
              <tr>
                <td colspan="8" class="no-result">
                  {{ $t('userInfo.userManagement.noRegisteredPage') }}
                </td>
              </tr>
            </tbody>
          </table>
          <div class="load" v-if="!applyList">
            <ui-loading-circle>
              {{ $t('common.loading') }}
            </ui-loading-circle>
          </div>
        </div>
      </content-box>
    </div>

    <dimmed v-if="isUserInfoSaved">
      <content-box class="user_saved" :width="'430px'">
        <i class="icon icon--check-green-size50"></i>
        <p class="title" v-html="$t('userInfo.userManagement.editInformation.success.title')"></p>
        <p class="desc" v-html="$t('userInfo.userManagement.editInformation.success.desc')"></p>
        <div class="btn__confirm">
          <ui-button
            @click="isUserInfoSaved = false">{{ $t('common.confirm') }}</ui-button>
        </div>
      </content-box>
    </dimmed>

    <dimmed v-if="isUserRoleEditing">
      <content-box class="user_approve" :width="'830px'" :height="'543px'">
        <button class="close" @click="isUserRoleEditing = false">
          <i class="icon icon--close14"></i>
        </button>
        <div v-if="approveStep === 1">
          <h3 v-html="$t('userInfo.requestManagement.approve.step1.title')"></h3>
          <div class="select">
            <div class="half">
              <span class="icon icon--localmanager"></span>
              <p class="title">{{ $t('userInfo.requestManagement.approve.step1.localManager.title') }}</p>
              <p class="desc">{{ $t('userInfo.requestManagement.approve.step1.localManager.desc') }}</p>
              <ui-button
                :primary2="true"
                @click="onApproveUserStep2(true)">{{ $t('common.next') }}</ui-button>
            </div>
            <div class="half">
              <span class="icon icon--viewer"></span>
              <p class="title">{{ $t('userInfo.requestManagement.approve.step1.viewer.title') }}</p>
              <p class="desc">{{ $t('userInfo.requestManagement.approve.step1.viewer.desc') }}</p>
              <ui-button
                :primary2="true"
                @click="onApproveUserStep2(false)">{{ $t('common.next') }}</ui-button>
            </div>
          </div>
        </div>
        <div v-else-if="approveStep === 2">
          <h3 v-html="$t('userInfo.requestManagement.approve.step2.title')"></h3>
          <country-code-selectbox
            :value="model.originalSiteCodeList"
            :width="'770px'"
            :height="'353px'"
            :full="true"
            @change="onSiteCodeChange"/>
          <div class="buttons">
            <ui-button
              :primary1="true"
              @click="approveStep = 1">{{ $t('common.back') }}</ui-button>
            <ui-button
              :primary2="true"
              :disabled="!canGoStep3"
              @click="approveStep = 3">{{ $t('common.next') }}</ui-button>
          </div>
        </div>
        <div v-else-if="approveStep === 3">
          <h3 v-html="$t('userInfo.requestManagement.approve.step3.title')"></h3>
          <ui-check-selectbox
            id="signUpTimezone"
            :value="model.originalSiteCodeList.includes('ALL') ? allEtcSiteCodeList : model.etcSiteCodeList"
            :width="'770px'"
            :height="'353px'"
            :full="true"
            :unSelectAll="true"
            :disableCheckbox="model.originalSiteCodeList.includes('ALL')"
            :search="false"
            :maxHeight="321"
            :items="etcSiteCodeList"
            @input="onEtcSiteCodeChange"/>
          <div class="buttons">
            <ui-button
              :primary1="true"
              @click="approveStep = 2">{{ $t('common.back') }}</ui-button>
            <ui-button
              :primary2="true"
              @click="onUpdateUserInfo">{{ $t('common.apply') }}</ui-button>
          </div>
        </div>
      </content-box>
    </dimmed>
  </section>
</template>

<script>
import ContentBox from '@/components/ui/ContentBox'
import SortOptions from '@/components/ui/SortOptions'
import TextHighlight from '@/components/ui/TextHighlight'
import Dimmed from '@/components/ui/Dimmed'
import UiButton from '@/components/ui/UiButton'
import UiInputText from '@/components/ui/UiInputText'
import UiCheckSelectbox from '@/components/ui/UiCheckSelectbox'
import UiLoadingCircle from '@/components/ui/UiLoadingCircle.vue'
import CountryCodeSelectbox from '@/components/ui/CountryCodeSelectbox'
import TimezoneSelectbox from '@/components/ui/TimezoneSelectbox'
import countryCode from '@/data/country-code'
import { userInfo, updateUserInfo, checkDupEmail } from '@/api/user'
import { userApplyList, getEtcSiteCodeList } from '@/api/apply'
import { isValidEmail, isValidEmailDomain } from '@/libs/validation-helper'
import { mapState } from 'vuex'

export default {
  components: {
    ContentBox,
    SortOptions,
    TextHighlight,
    UiInputText,
    UiButton,
    UiCheckSelectbox,
    UiLoadingCircle,
    CountryCodeSelectbox,
    TimezoneSelectbox,
    Dimmed
  },
  props: [
    'selectedUser'
  ],
  data () {
    return {
      user: null,
      applyList: null,
      model: {
        email: null,
        branchName: null,
        timezone: null,
        timezoneName: null,
        updatePermission: null,
        originalSiteCodeList: null,
        etcSiteCodeList: null
      },
      modelError: {
        email: '',
        branchName: ''
      },
      isUserInfoSaved: false,

      isUserRoleEditing: false,
      approveStep: 1,
      canGoStep3: true,
      wasAllSelected: false,

      searchKeyword: '',
      hasResult: true,
      sortOptions: {
        column: 'domain',
        isAsc: true
      },

      allEtcSiteCodeList: [],
      etcSiteCodeList: [{
        value: '',
        text: '',
        items: []
      }]
    }
  },
  computed: {
    ...mapState([
      'userData'
    ]),
    countryCodeList () {
      let items = {}
      Object.entries(countryCode).map(([key, value]) => {
        Object.entries(value.country).map(([k, v]) => {
          items[k] = v
        })
      })
      return items
    },
    sortColumn () {
      return this.sortOptions.column
    },
    isSortAsc () {
      return this.sortOptions.isAsc
    }
  },
  watch: {
    searchKeyword (value) {
      if (!value.length) {
        this.hasResult = true
      } else {
        this.hasResult = false
      }

      this.applyList.forEach(item => {
        item.isHide = false
        if (value.length) {
          if (!(item.domain && item.domain.toLowerCase().includes(value)) &&
            !(item.siteCode && item.siteCode.toLowerCase().includes(value)) &&
            !(item.category && item.category.toLowerCase().includes(value)) &&
            !(item.pageName && item.pageName.toLowerCase().includes(value))
          ) {
            item.isHide = true
          } else {
            this.hasResult = true
          }
        }
      })
    },
    isUserRoleEditing (value) {
      if (value) {
        this.approveStep = 1
        this.model.updatePermission = this.user.updatePermission
        this.model.originalSiteCodeList = this.user.originalSiteCodeList
        this.model.etcSiteCodeList = this.user.etcSiteCodeList
        this.wasAllSelected = false
      }
    },
    approveStep (value) {
      if (value === 1) {
        this.onSiteCodeChange(this.user.originalSiteCodeList)
        this.model.updatePermission = this.user.updatePermission
        this.model.etcSiteCodeList = this.user.etcSiteCodeList
      } else if (value === 3) {
        if (this.model.originalSiteCodeList.includes('ALL')) {
          this.model.etcSiteCodeList = []
          this.wasAllSelected = true
        } else {
          if (this.wasAllSelected) {
            this.model.etcSiteCodeList = this.user.etcSiteCodeList
          }
          this.wasAllSelected = false
        }
      }
    }
  },
  methods: {
    selectedTimezone (value) {
      this.model.timezone = value.offset
      this.model.timezoneName = value.timezone
    },
    hideValidationErrorText (value) {
      if (value === 'all') {
        for (let item in this.modelError) {
          this.modelError[item] = ''
        }
      } else {
        this.modelError[value] = ''
      }
    },
    onUpdateUserInfo () {
      let isPassedValidationEmail = true
      let isAllPass = true

      if (this.model.email !== this.user.email) {
        if (!this.model.email) {
          this.modelError.email = 'requiredEmail'
          isPassedValidationEmail = false
        } else {
          const formatCheckResult = isValidEmail(this.model.email)
          const domainCheckResult = isValidEmailDomain(this.model.email)
          if (!formatCheckResult) {
            this.modelError.email = 'diffFormatEmail'
            isPassedValidationEmail = false
          } else if (!domainCheckResult) {
            this.modelError.email = 'notValidEmail'
            isPassedValidationEmail = false
          }
          if (isPassedValidationEmail) this.hideValidationErrorText('email')
        }
      }

      if (!this.model.branchName) {
        this.modelError.branchName = 'requiredCorporation2'
      }

      for (let item in this.modelError) {
        if (this.modelError[item]) isAllPass = false
      }

      if (this.model.email !== this.user.email) {
        if (isPassedValidationEmail) {
          checkDupEmail({ email: this.model.email }).then((response) => {
            if (response.okToUse) {
              this.modelError.email = ''
              if (isAllPass) {
                updateUserInfo({
                  userNum: this.user.userNum,
                  ...this.model
                }).then(result => {
                  this.isUserRoleEditing = false
                  this.isUserInfoSaved = true
                  this.loadUser()
                })
              }
            } else {
              this.modelError.email = 'alreadyUsingEmail'
            }
          })
        }
      } else {
        if (isAllPass) {
          updateUserInfo({
            userNum: this.user.userNum,
            ...this.model
          }).then(result => {
            this.isUserRoleEditing = false
            this.isUserInfoSaved = true
            this.loadUser()
          })
        }
      }
    },
    onSortOptionChange (columnName, isAsc) {
      this.applyList.sort(function (a, b) {
        return String(a[columnName]).localeCompare(String(b[columnName]))
      })
      if (!isAsc) {
        this.applyList.reverse()
      }
      this.sortOptions = {
        column: columnName,
        isAsc: isAsc
      }
    },
    onApproveUserStep2 (updatePermission) {
      this.model.updatePermission = updatePermission
      this.approveStep = 2
    },
    onSiteCodeChange (value) {
      if (value.length) {
        this.canGoStep3 = true
      } else {
        this.canGoStep3 = false
      }
      this.model.originalSiteCodeList = value
    },
    onEtcSiteCodeChange (value) {
      this.model.etcSiteCodeList = value
    },
    onViewApplyClick (aid) {
      const to = this.$router.resolve({ name: 'heatmap-report', params: { aid: aid } })
      window.open(to.href, '_blank')
    },
    load () {
      this.loadUser()

      userApplyList({
        userNum: this.selectedUser
      }).then(result => {
        result.forEach(item => {
          item.isHide = false
        })
        this.applyList = result
      })
    },
    loadUser () {
      userInfo({
        userNum: this.selectedUser
      }).then(result => {
        this.user = result

        this.model.email = this.user.email
        this.model.branchName = this.user.branchName
        this.model.timezone = this.user.timezone
        this.model.timezoneName = this.user.timezoneName
        this.model.updatePermission = this.user.updatePermission
        this.model.originalSiteCodeList = this.user.originalSiteCodeList
        this.model.etcSiteCodeList = this.user.etcSiteCodeList
        this.user.originalSiteCodeList.sort()
        this.user.etcSiteCodeList.sort()
      })
    }
  },
  created () {
    this.load()

    getEtcSiteCodeList('ETC').then(result => {
      this.etcSiteCodeList[0].items = []
      result.forEach(item => {
        this.etcSiteCodeList[0].items.push(
          {
            value: item.siteCode,
            text: ''
          }
        )
      })
      this.allEtcSiteCodeList = []
      this.etcSiteCodeList[0].items.map(item => {
        this.allEtcSiteCodeList.push(item.value)
      })
    })
  }
}
</script>

<style lang="scss" scoped="scoped">
.user_management__detail {
  padding-top: 6px;
  margin-top: -6px;
  height: calc(100vh - 86px);
  overflow-y: auto;

  .content__header {
    font-size: 26px;
    font-weight: bold;
    color: #444444;
    margin-left: 30px;
    margin-right: 30px;
    padding-bottom: 12px;

    h2 {
      margin-top: -3px;

      .ui-btn {
        transform: translateY(-2px);
        margin-right: 4px;
        vertical-align: middle;
      }
    }
  }

  .content__body {
    padding: 0 30px;

    .content__top {
      h4 {
        margin: 25px 25px 33px 25px;
        color: #2f2f2f;
        font-size: 15px;
        font-weight: bold;
      }

      .content__user-info {
        box-sizing: border-box;
        position: relative;
        margin-bottom: 30px;
        float: left;
      }

      .content__user-permission {
        box-sizing: border-box;
        position: relative;
        margin-left: 30px;
        float: right;
      }

      label {
        display: inline-block;
        width: 140px;
        padding-left: 30px;
        font-size: 13px;
        color: #767676;
        vertical-align: top;
        line-height: 1.56;
        padding-top: 10px;
        margin-bottom: 23px;
        font-weight: bold;
        word-break: keep-all;
      }

      .input {
        display: inline-block;
        width: calc(100% - 220px);
        padding-right: 50px;
        margin-bottom: 23px;

        & /deep/ label.label {
          display: none;
        }

        & /deep/ .input--wrap.above-input {
          margin-top: 0;
        }

        input[type=text],
        input[type=password] {
          height: 38px;
          line-height: 36px;
          border-radius: 6px;
          border: solid 1px #bbbbbb;
          background-color: #ffffff;
          padding: 11px 35px 13px 12px;
          box-sizing: border-box;
          font-size: 14px;
          color: #2f2f2f;
          width: 100%;
        }
      }

      .input__list {
        margin-top: -13px;

        .readonly {
          max-width: calc(100% - 200px);
        }
      }

      .readonly {
        @include textEllipsis;
        display: inline-block;
        max-width: 100%;
        padding: 13px 0;
        color: #2f2f2f;
        font-size: 14px;
      }

      .site-code-list {
        display: inline-block;
        width: calc(100% - 212px);
        height: 103px;
        margin: 12px 40px 13px 0;
        overflow-y: auto;
        border: 1px solid #d7d7d7;
        background: #f4f4f4;
        border-radius: 5px;
        padding: 8px 0;
        box-sizing: border-box;

        &.long {
          height: 142px;
          margin-bottom: 36px;
        }

        li {
          @include textEllipsis;
          font-size: 13px;
          float: left;
          width: 50%;
          padding: 8px 20px;
          box-sizing: border-box;
          color: #2f2f2f;
        }
      }

      .button--wrap {
        position: absolute;
        bottom: 25px;
        left: 0;
        width: 100%;
        text-align: center;

        .ui-btn {
          width: 100px;
        }
      }
    }

    .content__apply-list {
      margin-bottom: 30px;
      position: relative;
      clear: both;

      h4 {
        padding: 25px 25px 20px 25px;
        color: #2f2f2f;
        font-size: 18px;
        font-weight: bold;
      }

      .input__search {
        position: absolute;
        right: 20px;
        top: 13px;

        .search-input {
          width: 240px;
          height: 38px;
          line-height: 36px;
          border-radius: 20px;
          border: solid 1px #bbbbbb;
          background-color: #ffffff;
          padding-left: 18px;
          padding-right: 26px;
          box-sizing: border-box;
          font-size: 14px;
          color: #2f2f2f;

          &:focus {
            border: solid 1px #289eef;
          }
        }

        .icon {
          position: absolute;
          right: 11px;
          transform: translateY(-50%);
          top: 18px;
        }
      }

      .item__body {
        padding: 0 25px 25px 25px;

        .load {
          height: 240px;
          border-bottom: 1px solid #dddddd;
          position: relative;

          .loading {
            @include positionXYcenter;
          }
        }

        table {
          font-size: 13px;
          width: 100%;
          table-layout: fixed;
        }

        table thead tr {
          height: 50px;
          background-color: #555555;
          line-height: 1.23;
          color: #ffffff;
          text-align: center;

          th {
            white-space: nowrap;
          }

          th:nth-of-type(1) {
            position: relative;

            &:before {
              content: "";
              display: inline-block;
              width: 15px;
              height: 50px;
              position: absolute;
              top: 0;
              left: -5px;
              background-color: #555;
              border-radius: 5px;
            }
          }

          th:nth-last-of-type(1) {
            position: relative;

            &:after {
              content: "";
              display: inline-block;
              width: 10px;
              height: 50px;
              position: absolute;
              top: 0;
              right: -5px;
              background-color: #555;
              border-radius: 5px;
            }
          }

          .align-left {
            text-align: left;
            padding-left: 10px;
          }

          .align-right {
            text-align: right;
            padding-right: 2px;
          }
        }

        table tbody tr {
          height: 80px;
          text-align: center;
          border-bottom: 1px solid #ebebeb;
          box-sizing: border-box;
          color: #2f2f2f;

          &:nth-of-type(1) {
            border-top: none;
          }

          &:nth-last-of-type(1) {
            border-bottom: 1px solid #ebebeb;
          }

          &:not(.deleted):hover {
            background-color: #f4f4f4;
          }

          &.checked:not(.deleted) {
            border: solid 1px #289eef;
            outline: solid 3px rgba(40, 158, 239, 0.18);
          }

          .align-left {
            text-align: left;
            padding-left: 10px;
            box-sizing: border-box;
          }

          .align-right {
            text-align: right;
            padding-right: 15px;
            box-sizing: border-box;
          }

          .no-result {
            color: #767676;
            height: 236px;
          }

          .page-name {
            font-size: 14px;
            font-weight: bold;
            line-height: 16px;
            color: #2f2f2f;
            height: 16px;
            margin-bottom: -2px;
            display: block;
          }

          .page-url {
            margin-top: 14px;
            font-size: 13px;
            line-height: 1;
            color: #666666;
            vertical-align: text-bottom;

            span {
              height: 15px;

              &.match-type {
                display: inline-block;
                margin-right: 8px;
                font-size: 12px;
                font-weight: bold;
                line-height: 15px;
                height: 15px;
                color: #ffffff;
                padding: 1px 5px;
                border-radius: 4px;
                background-color: #8d8d8d;
              }

              &.url {
                width: calc(100% - 70px);
                vertical-align: middle;
                display: inline-block;
              }
            }
          }

          .data__page {
            cursor: pointer;
          }

          .data__status,
          /deep/ .selected-item span,
          /deep/ .item-name {
            font-weight: bold;

            &.run {
              color: $stateRunColor;
            }

            &.stop {
              color: $stateStopColor;
            }

            &.error {
              color: $stateUnInstalledColor;
            }

            &.uninstalled {
              color: $stateUnInstalledColor;
            }

            &.endByPvLimit {
              color: $stateEndColor;
            }

            &.endByAnalysisDate {
              color: $stateEndColor;
            }

            &.reservation {
              color: $stateReservationColor;
            }

            &.pending {
              color: $pendingColor;
            }
          }

          .data__start-date {
            color: #666;
          }

          .data__end-date {
            color: #666;
          }

          .data__confirm {
            button {
              position: relative;
              width: 75px;
              height: 34px;
              border-radius: 20px;
              border: solid 1px #57ab00;
              background-color: #ffffff;
              box-sizing: border-box;
              text-align: right;
              padding-right: 13px;
              font-weight: bold;
              line-height: 32px;
              color: #4a8500;

              &:hover {
                background-color: rgba(0, 0, 0, 0.06);
              }

              .icon {
                position: absolute;
                top: 50%;
                left: 11px;
                transform: translateY(-50%);
              }
            }
          }
        }
      }
    }
  }

  .user_approve {
    color: #444444;

    .close {
      position: absolute;
      top: 12px;
      right: 12px;
    }

    h3 {
      font-size: 20px;
      padding: 30px;
      font-weight: bold;
    }

    .select {
      margin-top: 70px;
      padding: 0 30px;
      height: 304px;

      .half {
        float: left;
        width: calc(50% - 1px);
        height: 100%;
        text-align: center;
        border-left: 1px solid #d8d8d8;

        &:first-child {
          border-right: 1px solid #d8d8d8;
          border-left: none;
        }

        .icon {
          width: 80px;
          height: 71px;
          margin: 15px 0;
        }

        .title {
          font-size: 20px;
          width: 216px;
          margin: 10px auto;
          font-weight: bold;
        }

        .desc {
          color: #2f2f2f;
          line-height: 1.54;
          font-size: 13px;
          height: 105px;
          width: 226px;
          margin: 0 auto;
        }

        .ui-btn {
          width: 156px;
        }
      }
    }

    .ui_checkselect {
      margin: 20px 30px;
    }

    .buttons {
      text-align: center;
      margin-top: 4px;

      .ui-btn {
        margin: 0 5px;
      }
    }
  }

  .user_saved {
    padding: 50px 30px 25px;
    text-align: center;

    .title {
      margin-top: 15px;
      font-size: 20px;
      font-weight: bold;
      line-height: 1;
      text-align: center;
      color: #444444;
    }
    .desc {
      margin-top: 12px;
      font-size: 14px;
      line-height: 1.43;
      color: #2f2f2f;
    }
    .btn__confirm {
      margin-top: 50px;
    }
  }
}
</style>
